import React, { BaseSyntheticEvent } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";

interface EmailSettingsSectionProps {
  smtpSettings: SmtpCustomConfiguration;
  setSmtpSettings: (smtpSettings: SmtpCustomConfiguration) => void;
  smtpSettingsChanged: boolean;
  successTest: boolean | null;
  setSuccessTest: (success: boolean | null) => void;
}
const EmailSettingsSection: React.FC<EmailSettingsSectionProps> = ({
  smtpSettings,
  setSmtpSettings,
  smtpSettingsChanged,
  successTest,
  setSuccessTest,
}) => {
  const handleTestSmtp = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!smtpSettings) {
      alert("Por favor, configure as opções de SMTP antes de testar.");
      return;
    }
    if (
      !smtpSettings.from_email ||
      !smtpSettings.from_name ||
      smtpSettings.from_email.trim() === "" ||
      smtpSettings.from_name.trim() === ""
    ) {
      alert("Por favor, preencha o endereço e nome do remetente.");
      return;
    }
    if (!smtpSettings.use_custom_email) {
      alert(
        "Para testar a configuração de SMTP, ative a opção 'Ativar Email Personalizado'.",
      );
      return;
    }
    if (
      !smtpSettings.smtp_host ||
      !smtpSettings.smtp_port ||
      (smtpSettings.smtp_authentication &&
        (!smtpSettings.smtp_username || !smtpSettings.smtp_password))
    ) {
      alert("Por favor, preencha todos os campos obrigatórios de SMTP.");
      return;
    }
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true; // Desabilita o botão para evitar múltiplos cliques
    button.classList.add("is-loading"); // Adiciona a classe de loading

    const response = api.post(
      API_URLS.SETTINGS + "/_action/smtp-test",
      smtpSettings,
    );
    response
      .then((res) => {
        if (res.data) {
          alert("Configuração de SMTP testada com sucesso!");
          setSuccessTest(true);
        } else {
          alert("Erro ao testar configuração de SMTP: " + res.data.message);
          setSuccessTest(false);
        }
      })
      .catch((error) => {
        console.warn("Erro ao testar configuração de SMTP:", error);
        alert(
          "Erro ao testar configuração de SMTP. Verifique o console para mais detalhes.",
        );
        setSuccessTest(false);
      })
      .finally(() => {
        button.disabled = false; // Reabilita o botão
        button.classList.remove("is-loading"); // Remove a classe de loading
      });
  };

  return (
    <fieldset>
      <legend className="title is-4">Configurações de Email</legend>
      <div className="field">
        <span className="tip text-xs has-text-grey">
          O email será enviado através do email padrão do sistema, no entanto,
          você pode personalizar o endereço de resposta aos emails enviados pelo
          sistema aos seus clientes.
          <br />
          Se você quiser personalizar o remetente, ative a opção "Ativar Email
          Personalizado" e preencha os campos necessários para configurar o seu
          servidor SMTP.
        </span>
      </div>
      <div className="field is-horizontal">
        <div className="field-label is-normal is-flex-grow-4">
          <label className="label">Ativar Email Personalizado</label>
        </div>
        <div className="field-body">
          <div className="field is-grouped">
            <div className="control">
              <label className="radio">
                <input
                  type="radio"
                  name="useCustomSmtp"
                  checked={smtpSettings?.use_custom_email === true}
                  onChange={() => {
                    setSmtpSettings({
                      ...smtpSettings,
                      use_custom_email: true,
                    });
                  }}
                />
                &nbsp;Sim
              </label>
              <label className="radio ml-4">
                <input
                  type="radio"
                  name="useCustomSmtp"
                  checked={smtpSettings?.use_custom_email === false}
                  onChange={() => {
                    setSmtpSettings({
                      ...smtpSettings,
                      use_custom_email: false,
                    });
                  }}
                />
                &nbsp;Não
              </label>
            </div>
          </div>
        </div>
      </div>
      {smtpSettings?.use_custom_email ? (
        <>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Endereço do Remetente</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    className={`input ${smtpSettingsChanged && (smtpSettings.from_email?.trim() ?? "") === "" ? "is-danger" : ""}`}
                    type="email"
                    name="fromEmail"
                    value={smtpSettings?.from_email || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        from_email: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Nome do Remetente</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    className={`input ${smtpSettingsChanged && (smtpSettings.from_name?.trim() ?? "") === "" ? "is-danger" : ""}`}
                    type="text"
                    name="fromName"
                    value={smtpSettings?.from_name || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        from_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">SMTP Endereço</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control is-expanded">
                  <input
                    className={`input ${smtpSettingsChanged && smtpSettings.use_custom_email && (smtpSettings.smtp_host?.trim() ?? "") === "" ? "is-danger" : ""}`}
                    type="text"
                    name="smtpHost"
                    placeholder="smtp.gmail.com"
                    value={smtpSettings?.smtp_host || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        smtp_host: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="field-label is-normal">
                  <label className="label">SMTP Porta</label>
                </div>
                <div className="control w-24">
                  <input
                    className={`input ${smtpSettingsChanged && smtpSettings.use_custom_email && !smtpSettings.smtp_port ? "is-danger" : ""}`}
                    type="number"
                    name="smtpPort"
                    placeholder="587"
                    value={smtpSettings?.smtp_port || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        smtp_port: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Autenticação SMTP</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <label className="radio">
                    <input
                      type="radio"
                      name="smtpAuthentication"
                      checked={smtpSettings?.smtp_authentication === true}
                      onChange={() => {
                        setSmtpSettings({
                          ...smtpSettings,
                          smtp_authentication: true,
                        });
                      }}
                    />
                    &nbsp;Sim
                  </label>
                </div>
                <div className="control ml-4">
                  <label className="radio">
                    <input
                      type="radio"
                      name="smtpNoAuthentication"
                      checked={smtpSettings?.smtp_authentication === false}
                      onChange={() => {
                        setSmtpSettings({
                          ...smtpSettings,
                          smtp_authentication: false,
                        });
                      }}
                    />
                    &nbsp;Não
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Utilizador</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control is-expanded">
                  <input
                    className={`input ${smtpSettingsChanged && smtpSettings.smtp_authentication && (smtpSettings.smtp_username?.trim() ?? "") === "" ? "is-danger" : ""}`}
                    type="text"
                    name="smtpUsername"
                    placeholder="xxxxx@gmail.com"
                    disabled={smtpSettings?.smtp_authentication === false}
                    value={smtpSettings?.smtp_username || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        smtp_username: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="field-label is-normal">
                  <label className="label">Chave</label>
                </div>
                <div className="control is-expanded">
                  <input
                    className={`input ${smtpSettingsChanged && smtpSettings.smtp_authentication && (smtpSettings.smtp_password?.trim() ?? "") === "" ? "is-danger" : ""}`}
                    type="password"
                    name="smtpPassword"
                    disabled={smtpSettings?.smtp_authentication === false}
                    value={smtpSettings?.smtp_password || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        smtp_password: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Segurança</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <label className="radio">
                    <input
                      type="radio"
                      name="smtpSecureTls"
                      checked={smtpSettings?.smtp_secure_tls === true}
                      onChange={() => {
                        setSmtpSettings({
                          ...smtpSettings,
                          smtp_secure_tls: true,
                        });
                      }}
                    />
                    &nbsp;TLS
                  </label>
                </div>
                <div className="control ml-4">
                  <label className="radio">
                    <input
                      type="radio"
                      name="smtpSecureSsl"
                      checked={smtpSettings?.smtp_secure_ssl === true}
                      onChange={() => {
                        setSmtpSettings({
                          ...smtpSettings,
                          smtp_secure_ssl: true,
                        });
                      }}
                    />
                    &nbsp;SSL
                  </label>
                </div>
                <div className="control ml-4">
                  <label className="radio">
                    <input
                      type="radio"
                      name="smtpNoSecurity"
                      checked={
                        smtpSettings?.smtp_secure_tls === false &&
                        smtpSettings?.smtp_secure_ssl === false
                      }
                      onChange={() => {
                        setSmtpSettings({
                          ...smtpSettings,
                          smtp_secure_tls: false,
                          smtp_secure_ssl: false,
                        });
                      }}
                    />
                    &nbsp;Nenhum
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4"></div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <button
                    className="button is-primary"
                    onClick={(event) => {
                      handleTestSmtp(event);
                      event.preventDefault();
                    }}
                  >
                    Testar Configuração
                  </button>
                </div>

                <div className="control">
                  <button
                    className="button is-light"
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        !window.confirm(
                          "Tem a certeza que deseja limpar as configurações de SMTP?",
                        )
                      ) {
                        return;
                      }
                      setSmtpSettings({
                        ...smtpSettings,
                        use_custom_email: true,
                        from_email: null,
                        from_name: null,
                        smtp_host: null,
                        smtp_port: null,
                        smtp_authentication: false,
                        smtp_username: null,
                        smtp_password: null,
                        smtp_secure_tls: false,
                        smtp_secure_ssl: false,
                      });
                    }}
                  >
                    Limpar Configuração
                  </button>
                </div>
                <div className="control">
                  {successTest !== null && (
                    <span
                      className={`tag ${successTest ? "is-success" : "is-danger"}`}
                    >
                      {successTest ? "Teste bem-sucedido!" : "Teste falhou!"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Email de Resposta</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    className={`input ${smtpSettingsChanged && smtpSettings?.reply_to_email?.trim() === "" ? "is-danger" : ""}`}
                    type="email"
                    name="replyEmail"
                    placeholder="xxxx@gmail.com"
                    value={smtpSettings?.reply_to_email || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        reply_to_email: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal is-flex-grow-4">
              <label className="label">Nome do Endereço de Resposta</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    className={`input ${smtpSettingsChanged && smtpSettings?.reply_to_name?.trim() === "" ? "is-danger" : ""}`}
                    type="text"
                    name="replyName"
                    placeholder="O meu Nome"
                    value={smtpSettings?.reply_to_name || ""}
                    onChange={(e) =>
                      setSmtpSettings({
                        ...smtpSettings,
                        reply_to_name: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </fieldset>
  );
};
export default EmailSettingsSection;
